import { lazy, Suspense, useMemo } from "react";
import { ErrorBoundary } from "@sentry/react";
import { ErrorFallback } from "../../patterns";
import { useFeatureFlag } from "../FeatureFlag/FeatureFlagsProvider";
import { DHSApprovalSubmissionLoading } from "./DHSApprovalSubmission.loading";
import { DHSApprovalSubmissionProps } from "./DHSApprovalSubmission.types";
import { addTranslationResources } from "./DHSApprovalSubmission.translations";
import i18next from "i18next";
import { useTranslation } from "react-i18next";

export const DHSApprovalSubmissionLoadable = lazy(
  () => import("./DHSApprovalSubmission").then(module => ({ default: module.DHSApprovalSubmission }))
);

type WrapperProps = Omit<DHSApprovalSubmissionProps, "i18next" | "useTranslation"> & {
  i18next?: typeof i18next
};

export const DHSApprovalSubmissionWrapper = (props: WrapperProps) => {
  const useTranslationFn = useTranslation;
  const i18nInstance = props.i18next || i18next;
  const dhsEnabled = useFeatureFlag("FEATURE_DHS_INTEGRATION");
  const { user } = props;

  useMemo(() => {
    addTranslationResources(i18nInstance);
  }, [i18nInstance]);

  // Rendering nothing also keeps the lazy chunk from being fetched for non-DHS tenants.
  if (!dhsEnabled || !user?.permissions?.createDhsApproval) return null;

  return (
    <ErrorBoundary fallback={<ErrorFallback />}>
      <Suspense fallback={<DHSApprovalSubmissionLoading />}>
        <DHSApprovalSubmissionLoadable
          {...(props as DHSApprovalSubmissionProps)}
          i18next={i18nInstance}
          useTranslation={useTranslationFn}
        />
      </Suspense>
    </ErrorBoundary>
  );
};

export { DHSApprovalSubmissionWrapper as DHSApprovalSubmission };
